import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, BellOff, BellRing, MessageCircle, CheckCheck, X, ArrowRight, Store } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { playSound, soundLabel, stopSound } from "@/lib/reminderSounds";

/**
 * One header bell for every alert in the app:
 *  - due calendar reminders (ding at their date+time, dismiss deletes them)
 *  - comment pings (a buyer commented on your listing), dismiss removes
 *    the ping without needing to open it.
 */
export default function NotificationBell({ onJumpToItem }) {
  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const loadReminders = useCallback(async () => {
    try {
      const r = await api.get("/reminders");
      setReminders(r.data || []);
    } catch { /* silent — no reminders / not logged in */ }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setNotifications(r.data?.notifications || []);
    } catch { /* silent — not logged in / none yet */ }
  }, []);

  useEffect(() => { loadReminders(); loadNotifications(); }, [loadReminders, loadNotifications]);

  useEffect(() => {
    const onCreated = () => loadReminders();
    window.addEventListener("notelosh:reminder-created", onCreated);
    return () => window.removeEventListener("notelosh:reminder-created", onCreated);
  }, [loadReminders]);

  useEffect(() => {
    const tick = () => setNow(Date.now());
    const iv = setInterval(() => { tick(); loadNotifications(); }, 30_000);
    const onFocus = () => { tick(); loadReminders(); loadNotifications(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [loadReminders, loadNotifications]);

  const due = reminders.filter((r) => {
    if ((r.recurrence || "none") !== "none") return false;
    const dt = new Date(`${r.date}T${r.time || "09:00"}:00`).getTime();
    return dt <= now;
  });

  // Ring once per reminder, same as before.
  const rungRef = useRef(new Set());
  useEffect(() => {
    const fresh = due.filter((r) => !rungRef.current.has(r.reminder_id));
    if (!fresh.length) return;
    fresh.forEach((r) => rungRef.current.add(r.reminder_id));
    const withSound = fresh.find((r) => (r.sound || "chime") !== "none");
    if (withSound) playSound(withSound.sound || "chime");
  }, [due]);

  const dismissReminder = async (rid) => {
    setReminders((rs) => rs.filter((r) => r.reminder_id !== rid));
    try { await api.delete(`/reminders/${rid}`); }
    catch { toast.error("Could not dismiss reminder"); loadReminders(); }
  };

  const dismissNotification = async (nid) => {
    setNotifications((ns) => ns.filter((n) => n.notification_id !== nid));
    try { await api.delete(`/notifications/${nid}`); }
    catch { toast.error("Could not dismiss notification"); loadNotifications(); }
  };

  const jumpReminder = (r) => {
    if (!r.title_id || !onJumpToItem) return;
    stopSound();
    setOpen(false);
    onJumpToItem({ title_id: r.title_id, item_id: r.item_id });
  };

  const openNotification = async (n) => {
    setOpen(false);
    setNotifications((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
    try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* ignore */ }
    navigate(n.group_id ? `/groups/${n.group_id}` : `/market?product=${n.product_id}`);
  };

  const markAllRead = async (e) => {
    e.stopPropagation();
    setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
  };

  const [muted, setMuted] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/push/prefs").then((r) => setMuted(!!r.data?.reminder_muted)).catch(() => setMuted(false));
  }, [open]);

  const togglePush = async (e) => {
    e.stopPropagation();
    const next = !muted;
    setMuted(next);
    try {
      await api.post("/push/prefs", { reminder_muted: next });
      toast.success(next ? "Push notifications off" : "Push notifications on");
    } catch {
      setMuted(!next);
      toast.error("Couldn't save that — try again?");
    }
  };

  const timeAgo = (iso) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  const unreadNotifCount = notifications.filter((n) => !n.read).length;
  const badgeCount = due.length + unreadNotifCount;
  const hasAny = badgeCount > 0;
  const nothingAtAll = due.length === 0 && notifications.length === 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell-btn"
          aria-label={hasAny ? `${badgeCount} alert${badgeCount > 1 ? "s" : ""}` : "Notifications"}
          className={`
            relative h-9 w-9 rounded-full flex items-center justify-center
            transition-all
            ${hasAny
              ? "bg-[#3B82F6]/15 text-[#93C5FD] hover:bg-[#3B82F6]/25 ring-1 ring-[#3B82F6]/40"
              : "bg-[#1E1E1E] text-[#9CA3AF] hover:bg-[#27272A] hover:text-[#F3F4F6]"}
          `}
        >
          <Bell className={`h-4 w-4 ${hasAny ? "animate-pulse" : ""}`} />
          {hasAny && (
            <span
              data-testid="notification-bell-badge"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-semibold flex items-center justify-center leading-none border border-[#0F0F11]"
            >
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] w-80 p-0"
      >
        <div className="px-3 py-2.5 border-b border-[#27272A] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Bell className="h-3.5 w-3.5 text-[#93C5FD]" />
            <span className="text-sm font-medium">Notifications</span>
          </div>
          <div className="flex items-center gap-2">
            {unreadNotifCount > 0 && (
              <button
                data-testid="notification-bell-mark-all-read"
                onClick={markAllRead}
                className="text-[10px] text-[#9CA3AF] hover:text-[#F3F4F6] inline-flex items-center gap-1"
              >
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
            <button
              type="button"
              data-testid="toggle-reminder-pushes"
              onClick={togglePush}
              disabled={muted === null}
              title={muted ? "Push notifications off — tap to turn on" : "Push notifications on — tap to turn off"}
              aria-label={muted ? "Turn push notifications on" : "Turn push notifications off"}
              className={`h-6 w-6 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${muted ? "text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[#27272A]" : "text-[#10B981] hover:bg-[#10B981]/10"}`}
            >
              {muted ? <BellOff className="h-3.5 w-3.5" /> : <BellRing className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {nothingAtAll ? (
          <div className="px-3 py-8 text-center text-sm text-[#9CA3AF]">
            Nothing here yet.<br />
            <span className="text-xs text-[#6B7280]">Reminders, buyer comments and new notes from sellers you follow show up here.</span>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {due.length > 0 && (
              <ul className="py-1">
                {due.map((r) => (
                  <li
                    key={`rem-${r.reminder_id}`}
                    data-testid="reminder-bell-item"
                    className="px-3 py-2.5 border-b border-[#1E1E1E] hover:bg-[#1A1A1A]"
                  >
                    <div className="flex items-start gap-2">
                      <Bell className="h-3.5 w-3.5 text-[#93C5FD] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        {r.title_id && onJumpToItem ? (
                          <button
                            type="button"
                            onClick={() => jumpReminder(r)}
                            data-testid={`reminder-bell-open-${r.reminder_id}`}
                            className="w-full text-left group"
                          >
                            <div className="text-sm text-[#F3F4F6] mb-1 break-words group-hover:text-[#93C5FD] transition-colors">
                              {r.text || "Reminder"}
                            </div>
                            <div className="text-[10px] text-[#3B82F6] mb-1 inline-flex items-center gap-1">
                              <ArrowRight className="h-2.5 w-2.5" /> Open the note
                            </div>
                          </button>
                        ) : (
                          <div className="text-sm text-[#F3F4F6] mb-1 break-words">{r.text || "Reminder"}</div>
                        )}
                        <span className="text-[10px] text-[#6B7280]">
                          {r.date} · {r.time || "09:00"}
                          {(r.sound || "chime") !== "none" && ` · ${soundLabel(r.sound || "chime")}`}
                        </span>
                      </div>
                      <button
                        data-testid="reminder-bell-dismiss"
                        onClick={() => dismissReminder(r.reminder_id)}
                        aria-label="Dismiss reminder"
                        className="h-5 w-5 shrink-0 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[#27272A] transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {notifications.length > 0 && (
              <ul className="py-1">
                {notifications.map((n) => (
                  <li
                    key={`ntf-${n.notification_id}`}
                    data-testid="notification-bell-item"
                    className={`px-3 py-2.5 border-b border-[#1E1E1E] last:border-b-0 hover:bg-[#1A1A1A] transition-colors ${!n.read ? "bg-[#3B82F6]/5" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#3B82F6] shrink-0" />}
                      {n.type === "new_listing"
                        ? <Store className={`h-3.5 w-3.5 text-[#60A5FA] mt-0.5 shrink-0 ${n.read ? "ml-3.5" : ""}`} />
                        : <MessageCircle className={`h-3.5 w-3.5 text-[#34D399] mt-0.5 shrink-0 ${n.read ? "ml-3.5" : ""}`} />}
                      <button
                        type="button"
                        onClick={() => openNotification(n)}
                        data-testid={`notification-bell-open-${n.notification_id}`}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="text-sm text-[#F3F4F6] break-words">
                          {n.type === "group_join_request" ? (
                            <><span className="font-medium">{n.from_user_name}</span> asked to join <span className="font-medium">{n.product_name}</span></>
                          ) : n.type === "group_approved" ? (
                            <>You're now a member of <span className="font-medium">{n.product_name}</span></>
                          ) : n.type === "new_listing" ? (
                            <><span className="font-medium">{n.from_user_name}</span> published <span className="font-medium">{n.product_name}</span></>
                          ) : (
                            <><span className="font-medium">{n.from_user_name}</span> commented on <span className="font-medium">{n.product_name}</span></>
                          )}
                        </div>
                        {n.comment_text && (
                        <div className="text-xs text-[#9CA3AF] break-words mt-0.5 line-clamp-2">
                          "{n.comment_text}"
                        </div>
                        )}
                        <div className="text-[10px] text-[#6B7280] mt-1">{timeAgo(n.created_at) === "now" ? "just now" : `${timeAgo(n.created_at)} ago`}</div>
                      </button>
                      <button
                        data-testid={`notification-bell-dismiss-${n.notification_id}`}
                        onClick={() => dismissNotification(n.notification_id)}
                        aria-label="Dismiss notification"
                        className="h-5 w-5 shrink-0 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[#27272A] transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
